import React from "react";
import { AlertTriangle, CheckCircle2, Clock3, Inbox } from "lucide-react";
import { STAGE_STYLES } from "@/lib/rights";

const Kpi = ({ icon: Icon, label, value, sub, tone, testId, delay }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 rr-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Icon className={`w-4 h-4 ${tone}`} />
    </div>
    <div className={`mt-3 text-3xl font-bold tracking-tight font-mono ${tone}`} data-testid={`${testId}-value`}>{value}</div>
    <div className="text-xs text-slate-500 mt-1">{sub}</div>
  </div>
);

export const RightsKpis = ({ d }) => {
  if (!d) return <div className="h-[132px] rounded-xl bg-slate-100 animate-pulse" />;
  const fmt = (v, unit) => (v == null ? "—" : `${v}${unit}`);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rights-kpis">
      <Kpi icon={Inbox} label="Open requests" value={d.open} tone="text-[#003366]" delay={0}
           sub={`${d.due_soon} due in the next 7 days · ${d.total} total`} testId="kpi-open" />
      <Kpi icon={AlertTriangle} label="Overdue" value={d.overdue} tone={d.overdue ? "text-red-600" : "text-slate-400"} delay={60}
           sub={d.overdue ? "Past target date and still open" : "Nothing past its target date"} testId="kpi-overdue" />
      <Kpi icon={Clock3} label="Avg response time" value={fmt(d.avg_response_days, "d")} tone="text-[#008080]" delay={120}
           sub={`Received → response delivered · ${d.responded} responded`} testId="kpi-response-time" />
      <Kpi icon={CheckCircle2} label="Closure performance" value={fmt(d.on_time_pct, "%")} delay={180}
           tone={d.on_time_pct == null ? "text-slate-400" : d.on_time_pct >= 90 ? "text-emerald-600" : "text-amber-600"}
           sub={`${d.closed_on_time} on time · ${d.closed_late} late · avg ${fmt(d.avg_closure_days, "d")} to close`}
           testId="kpi-closure" />
    </div>
  );
};

export const StagePipeline = ({ d, active, onPick }) => {
  if (!d) return null;
  const stages = Object.entries(d.by_stage);
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="rights-stage-pipeline">
      {stages.map(([k, s], i) => (
        <button key={k} onClick={() => onPick(active === k ? null : k)} data-testid={`pipeline-stage-${k.toLowerCase()}`}
                className={`text-left rounded-lg border px-3 py-2.5 transition-[background-color,box-shadow,border-color] ${
                  active === k ? "border-[#003366] bg-white shadow-sm" : "border-slate-200 bg-white/70 hover:bg-white"}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400">
            <span>{String(i + 1).padStart(2, "0")}</span>
            <span className={`w-1.5 h-1.5 rounded-full border ${STAGE_STYLES[k]}`} />
          </div>
          <div className="text-[12px] font-semibold text-[#003366] mt-0.5">{s.label}</div>
          <div className="text-lg font-mono font-bold text-[#003366]">{s.count}</div>
        </button>
      ))}
    </div>
  );
};
